import { zodResolver } from '@hookform/resolvers/zod';
import { type CatalogItem } from '@kalos/kalos-rpc';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  SimpleSelect,
  Switch,
  Textarea,
} from '@kalos/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  type CatalogItemSchema,
  catalogItemSchema,
  getCatalogItemFormDefaultValues as getDefaultValues,
  useCatalogItemFormOptions,
  useSaveCatalogItem,
} from './catalogItemFormShared';

interface CatalogItemFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: CatalogItem | null;
}

export const CatalogItemFormDialog = ({ open, onOpenChange, item }: CatalogItemFormDialogProps) => {
  const form = useForm<CatalogItemSchema>({
    resolver: zodResolver(catalogItemSchema),
    defaultValues: getDefaultValues(item),
  });

  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues(item));
    }
  }, [open, item, form]);

  const { categoryOptions, tradeTypes } = useCatalogItemFormOptions();

  const {
    save: onSubmit,
    isSaving,
    isEdit,
  } = useSaveCatalogItem({ item, onSaved: () => onOpenChange(false) });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogTitle>{isEdit ? 'Edit Tool' : 'Add Tool'}</DialogTitle>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="brand"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Brand</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="categoryId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <SimpleSelect
                      onChange={(value) => {
                        field.onChange(value ? Number(value) : 0);
                      }}
                      values={categoryOptions}
                      selectedValue={field.value ? field.value.toString() : ''}
                      placeholder="Select category"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="externalLink"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Link</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="tradeTypeIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trades</FormLabel>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {(tradeTypes ?? []).map((trade) => (
                      <label key={trade.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={field.value.includes(trade.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              field.onChange([...field.value, trade.id]);
                            } else {
                              field.onChange(field.value.filter((id) => id !== trade.id));
                            }
                          }}
                        />
                        {trade.name}
                      </label>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isEdit && (
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="!mt-0">Active</FormLabel>
                  </FormItem>
                )}
              />
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
